import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { TextField, StateSelect } from "../../components/fields";
import {
  Field, MoneyField, PhoneField, Select, ErrorNote,
} from "../../components/ui";
import { loadCounties, surtaxLabel, type County } from "../../lib/counties";
import { countyForZip } from "../../lib/zipCounty";
import { parseMoney, centsToInput } from "../../lib/money";
import { checkRequired, checkZip, checkEmail, checkPhone } from "../../lib/validation";
import { SaveBar } from "../../components/save";
import { mustSave, useSaveState } from "../../lib/saving";

/**
 * Dealer settings. One row. These values get stamped onto every printed form,
 * so they are entered once here rather than typed onto each deal.
 */
const BLANK = {
  legalName: "", dba: "", flhsmvLicenseNo: "", salesTaxRegNo: "",
  street: "", city: "", state: "FL", zip: "", county: "",
  phone: "", email: "",
  predelivery: "", efiling: "", title: "", tempTag: "", licenseReg: "",
  tagTransfer: "",
};

export default function Settings() {
  const [f, setF] = useState(BLANK);
  /* What the database holds, so "Unsaved changes" means changed. */
  const [savedF, setSavedF] = useState(BLANK);
  const [counties, setCounties] = useState<County[]>([]);
  const [errs, setErrs] = useState<Record<string, string | null>>({});
  const [err, setErr] = useState<string | null>(null);
  const [exists, setExists] = useState(false);
  const dirty = JSON.stringify(f) !== JSON.stringify(savedF);
  const save_ = useSaveState({ dirty, what: "the settings" });

  useEffect(() => { loadCounties().then(setCounties).catch(() =>
      setErr("Could not load the county list. Check your connection "
        + "and reopen this page.")); }, []);

  useEffect(() => {
    supabase.from("dealer_settings").select("*").maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        setExists(true);
        const loaded = {
          legalName: data.legal_name ?? "", dba: data.dba ?? "",
          flhsmvLicenseNo: data.flhsmv_license_no ?? "",
          salesTaxRegNo: data.sales_tax_reg_no ?? "",
          street: data.street ?? "", city: data.city ?? "",
          state: data.state ?? "FL", zip: data.zip ?? "", county: data.county ?? "",
          phone: data.phone ?? "", email: data.email ?? "",
          predelivery: centsToInput(data.default_predelivery_service_cents ?? 0),
          efiling: centsToInput(data.default_electronic_filing_cents ?? 0),
          title: centsToInput(data.default_title_cents ?? 0),
          tempTag: centsToInput(data.default_temp_tag_cents ?? 0),
          licenseReg: centsToInput(data.default_license_reg_cents ?? 0),
          tagTransfer: centsToInput(data.default_tag_transfer_cents ?? 0),
        };
        setF(loaded);
        setSavedF(loaded);
      });
  }, []);

  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  function zipChanged(v: string) {
    setF((s) => {
      const next = { ...s, zip: v };
      const hit = countyForZip(v);
      if (hit && !s.county) next.county = hit.county;
      return next;
    });
  }

  async function save() {
    const next = {
      legalName: checkRequired(f.legalName, "Legal name"),
      flhsmvLicenseNo: checkRequired(f.flhsmvLicenseNo, "Dealer license"),
      salesTaxRegNo: checkRequired(f.salesTaxRegNo, "Sales tax registration"),
      street: checkRequired(f.street, "Street"),
      city: checkRequired(f.city, "City"),
      zip: checkZip(f.zip),
      county: checkRequired(f.county, "County"),
      phone: checkPhone(f.phone) ?? checkRequired(f.phone, "Phone"),
      email: checkEmail(f.email) ?? checkRequired(f.email, "Email"),
    };
    setErrs(next);
    if (!Object.values(next).every((e) => e === null)) {
      save_.fail("Fix the fields marked in red, then press Save again.");
      return;
    }

    const sent = f;
    const row = {
      id: true,
      legal_name: f.legalName.trim(),
      dba: f.dba.trim() || null,
      flhsmv_license_no: f.flhsmvLicenseNo.trim(),
      sales_tax_reg_no: f.salesTaxRegNo.trim(),
      street: f.street.trim(), city: f.city.trim(),
      state: f.state.toUpperCase(), zip: f.zip.trim(), county: f.county,
      phone: f.phone, email: f.email.trim(),
      default_predelivery_service_cents: parseMoney(f.predelivery) ?? 0,
      default_electronic_filing_cents: parseMoney(f.efiling) ?? 0,
      default_title_cents: parseMoney(f.title) ?? 0,
      default_temp_tag_cents: parseMoney(f.tempTag) ?? 0,
      default_license_reg_cents: parseMoney(f.licenseReg) ?? 0,
      default_tag_transfer_cents: parseMoney(f.tagTransfer) ?? 0,
    };
    await save_.run(async () => {
      mustSave(await supabase.from("dealer_settings")
        .upsert(row, { onConflict: "id" }).select("id"));
      setExists(true);
      setSavedF(sent);
    }, { toast: "Settings saved" });
  }

  return (
    <>
      <div className="head"><h2>Settings</h2></div>
      <p className="sub">
        These go on every printed form. {exists ? "" : "Fill them in before writing a deal."}
      </p>

      <h3 className="subhead">Dealership</h3>
      <TextField label="Legal name" value={f.legalName} onChange={set("legalName")}
             error={errs.legalName} />
      <TextField label="DBA" value={f.dba} onChange={set("dba")} hint="If you trade under another name" />
      <div className="grid2">
        <Field label="FLHSMV dealer license" mono value={f.flhsmvLicenseNo}
               onChange={set("flhsmvLicenseNo")} error={errs.flhsmvLicenseNo}
               hint="e.g. VI/1152138/1" />
        <Field label="Sales tax registration" mono value={f.salesTaxRegNo}
               onChange={set("salesTaxRegNo")} error={errs.salesTaxRegNo} />
      </div>

      <h3 className="subhead">Address</h3>
      <TextField label="Street" value={f.street} onChange={set("street")} error={errs.street} />
      <div className="grid2">
        <TextField label="City" value={f.city} onChange={set("city")} error={errs.city} />
        <Field label="ZIP" inputMode="numeric" value={f.zip} onChange={zipChanged}
               error={errs.zip} />
      </div>
      <div className="grid2">
        <StateSelect label="State" required value={f.state} onChange={set("state")} />
        <Select label="County" value={f.county} onChange={set("county")}
                placeholder="Choose" error={errs.county}>
          {counties.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name} ({surtaxLabel(c.surtax_bps)})
            </option>
          ))}
        </Select>
      </div>
      <div className="grid2">
        <PhoneField label="Phone" value={f.phone} onChange={set("phone")} error={errs.phone} />
        <Field label="Email" type="email" value={f.email} onChange={set("email")}
               error={errs.email} />
      </div>

      <h3 className="subhead">Default fees</h3>
      <p className="sub">
        Prefilled on every deal, editable per deal. The first two are taxable in
        Florida; the rest are government charges and are not.
      </p>
      <div className="grid2">
        <MoneyField label="Dealer services" value={f.predelivery} onChange={set("predelivery")} />
        <MoneyField label="Electronic filing" value={f.efiling} onChange={set("efiling")} />
      </div>
      <div className="grid2">
        <MoneyField label="Title fee" value={f.title} onChange={set("title")} />
        <MoneyField label="Temp tag" value={f.tempTag} onChange={set("tempTag")} />
      </div>
      {/* Two charges, because they are two different transactions at the tax
          collector. The deal screen picks whichever the tag choice calls for. */}
      <div className="grid2">
        <MoneyField label="License and registration" value={f.licenseReg}
                    onChange={set("licenseReg")}
                    hint="A new plate" />
        <MoneyField label="Tag transfer" value={f.tagTransfer}
                    onChange={set("tagTransfer")}
                    hint="Moving a plate the buyer already has" />
      </div>

      {err && <ErrorNote>{err}</ErrorNote>}

      <SaveBar save={save_} onSave={() => void save()} label="Save settings"
               idle={exists ? "No unsaved changes" : "Not saved yet"} alwaysPrimary={!exists} />
    </>
  );
}
